import {
  getIsAudioMuted,
  setIsAudioMuted,
  getIsVideoDisabled,
  setIsVideoDisabled,
  getAudioVolume,
  setAudioVolume,
} from "../../../ucs"
import type { InitialCallState } from "../IFrameEvents"
import type {
  GetLocalMediaStateEvent,
  SetLocalMediaEvent,
  SetVolumeEvent,
} from "./incomingEvents"
import {
  createLocalMediaStateEvent,
  type AmnOutgoingEvent,
  type LocalMediaStateEvent,
} from "./outgoingEvents"
import { convertAmnVolumeToUcsVolume } from "./volumeConversion"
import type { VendorParams } from "../../../url/params/UrlParams"

type InitialMediaParams = Pick<
  VendorParams,
  "startCallOnMute" | "startWithVideo" | "initialVolume"
>

/**
 * This cache is responsible for caching any relevant call state changes that
 * occur during the connection process, and then synchronizing that state once
 * the call is connected. This ensures that if the user toggles their
 * audio/video or adjusts volume before the call is fully connected, those
 * changes will still be applied.
 */
export type InitialCallStateCache = {
  getInitialCallState: () => InitialCallState
  isAudioEnabled: () => boolean
  isVideoEnabled: () => boolean
  getInitialVolume: () => number
  handleSetLocalMediaEvent: (event: SetLocalMediaEvent) => void
  handleGetLocalMediaStateEvent: (event: GetLocalMediaStateEvent) => void
  handleSetVolumeEvent: (event: SetVolumeEvent) => void

  /**
   * Any changes to the call state that took place during the connection process
   * need to be synchronized once the call is connected.
   */
  synchronizeCallStateOnConnect: () => Promise<void>
}

export const initInitialCallStateCache = (
  postMessage: (event: AmnOutgoingEvent) => void,
  vendorUrlParams: InitialMediaParams = {}
): InitialCallStateCache => {
  let initialCallState: InitialCallState = {}

  const getInitialCallState = () => initialCallState

  const isAudioMuted = () =>
    initialCallState.startCallOnMute ?? vendorUrlParams.startCallOnMute ?? false

  const isVideoEnabled = () =>
    initialCallState.startWithVideo ?? vendorUrlParams.startWithVideo ?? true

  const isAudioEnabled = () => !isAudioMuted()

  const getInitialVolume = () =>
    initialCallState.initialVolume ?? vendorUrlParams.initialVolume ?? 100

  const setAudioEnabled = (enabled: boolean) => {
    const wasAudioEnabled = isAudioEnabled()
    initialCallState.startCallOnMute = !enabled
    // eslint-disable-next-line no-console
    console.log("Caching setLocalMedia event for audio prior to connecting", {
      startCallOnMute: initialCallState.startCallOnMute,
    })
    const newIsAudioEnabled = isAudioEnabled()
    if (wasAudioEnabled !== newIsAudioEnabled) {
      postMessage(createLocalMediaStateEvent("audio", newIsAudioEnabled))
    }
  }

  const setVideoEnabled = (enabled: boolean) => {
    const wasVideoEnabled = isVideoEnabled()
    initialCallState.startWithVideo = enabled
    // eslint-disable-next-line no-console
    console.log("Caching setLocalMedia event for video prior to connecting", {
      startWithVideo: initialCallState.startWithVideo,
    })
    const newIsVideoEnabled = isVideoEnabled()
    if (wasVideoEnabled !== newIsVideoEnabled) {
      postMessage(createLocalMediaStateEvent("video", newIsVideoEnabled))
    }
  }

  const handleSetLocalMediaEvent = (event: SetLocalMediaEvent) => {
    const { mediaType, enabled } = event.payload
    if (mediaType === "audio") {
      setAudioEnabled(enabled)
    } else if (mediaType === "video") {
      setVideoEnabled(enabled)
    } else {
      // eslint-disable-next-line no-console
      console.warn("Received setLocalMedia event with unknown mediaType", {
        mediaType,
      })
    }
  }

  /**
   * From the AMN docs:
   * Note that since getLocalMediaState can happen before the call is initialized or
   * ready, vendor should return the values that will be applied to the call, such as any
   * values passed in by query string parameters.
   */
  const handleGetLocalMediaStateEvent = (
    incomingEvent: GetLocalMediaStateEvent
  ) => {
    const { mediaType } = incomingEvent.payload
    let outgoingEvent: LocalMediaStateEvent

    if (mediaType === "audio") {
      outgoingEvent = createLocalMediaStateEvent(mediaType, isAudioEnabled())
    } else if (mediaType === "video") {
      outgoingEvent = createLocalMediaStateEvent(mediaType, isVideoEnabled())
    } else {
      // eslint-disable-next-line no-console
      console.warn("Received getLocalMediaState event with unknown mediaType", {
        mediaType,
      })
      return
    }

    // eslint-disable-next-line no-console
    console.log(
      "Responding to getLocalMediaState with cached initial state prior to connecting",
      { incomingEvent, outgoingEvent }
    )
    postMessage(outgoingEvent)
  }

  const handleSetVolumeEvent = ({ payload: { volume } }: SetVolumeEvent) => {
    initialCallState.initialVolume = convertAmnVolumeToUcsVolume(volume)
    // eslint-disable-next-line no-console
    console.log("Caching setVolume event prior to connecting", {
      initialVolume: initialCallState.initialVolume,
    })
  }

  const synchronizeCallStateOnConnect = async () => {
    const promises: Promise<void>[] = []

    if (initialCallState.startCallOnMute !== undefined) {
      const cachedIsAudioMuted = initialCallState.startCallOnMute
      promises.push(
        getIsAudioMuted().then((sessionIsAudioMuted) => {
          if (cachedIsAudioMuted !== sessionIsAudioMuted) {
            // eslint-disable-next-line no-console
            console.log("Synchronizing isAudioMuted with cached value", {
              cachedIsAudioMuted,
            })
            return setIsAudioMuted(cachedIsAudioMuted)
          }
        })
      )
    }

    if (initialCallState.startWithVideo !== undefined) {
      const cachedIsVideoDisabled = !initialCallState.startWithVideo
      promises.push(
        getIsVideoDisabled().then((sessionIsVideoDisabled) => {
          if (cachedIsVideoDisabled !== sessionIsVideoDisabled) {
            // eslint-disable-next-line no-console
            console.log("Synchronizing isVideoDisabled with cached value", {
              cachedIsVideoDisabled,
            })
            return setIsVideoDisabled(cachedIsVideoDisabled)
          }
        })
      )
    }

    if (initialCallState.initialVolume !== undefined) {
      const cachedVolume = initialCallState.initialVolume
      promises.push(
        getAudioVolume().then((sessionVolume) => {
          if (cachedVolume !== sessionVolume) {
            // eslint-disable-next-line no-console
            console.log("Synchronizing audioVolume with cached value", {
              cachedVolume,
            })
            return setAudioVolume(cachedVolume)
          }
        })
      )
    }

    await Promise.all(promises).catch((e) => {
      // eslint-disable-next-line no-console
      console.error("Failed to synchronize call state on connect", { e })
    })
  }

  return {
    getInitialCallState,
    isAudioEnabled,
    isVideoEnabled,
    getInitialVolume,
    handleSetLocalMediaEvent,
    handleGetLocalMediaStateEvent,
    handleSetVolumeEvent,
    synchronizeCallStateOnConnect,
  }
}
